import { memo, useCallback } from "react";
import { Accordion, ContentCard, IconButton, Row } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { modules } from "byzantine";
import type { MouseEvent } from "react";

import styles from "./InternationalWire.module.scss";

type Props = {
  templateId: API.SwiftWireTemplateId;
  onSelect?: (id: API.SwiftWireTemplateId) => void;
  selected?: boolean;
};

const FlashUpdated = ({
  versionId,
}: {
  versionId: API.SwiftWireTemplate["version_id"];
}) => {
  const { l10n } = useLocalization();
  const updated =
    modules.internationalWires.useTemplateUpdated(versionId);
  return updated ? (
    <p className={styles.updated}>
      {l10n.getString("message-template-updated")}
    </p>
  ) : null;
};

function TemplateListCard({
  templateId,
  onSelect,
  selected = false,
}: Props) {
  const { collapsed, onToggle } =
    modules.internationalWires.useSwiftWireTemplatePreview();

  const details =
    modules.internationalWires.useSwiftWireTemplateDetails(templateId);

  const handleCardClick = useCallback(() => {
    onSelect?.(templateId);
  }, [onSelect, templateId]);

  const handleChevronToggle = useCallback(() => {
    onToggle();
  }, [onToggle]);

  if (!details) {
    return null;
  }
  const {
    templateDetails,
    recipientDetails,
    bankDetails,
    title,
    subheader,
    versionId,
  } = details;

  const renderSummary = useCallback(
    (isOpen: boolean) => (
      <div
        className={`${styles.previewCardSummary}`}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleCardClick();
          }
        }}
      >
        <ContentCard kind="bordered" paddingSize="l">
          <div className={styles.previewCardHeader}>
            <Row alignItems="center" justifyContent="space-between">
              <Row.Item>
                <div className={styles.previewCardTitleGroup}>
                  <h4 className={styles.previewCardTitle}>{title}</h4>
                  <p className={styles.previewCardSubtitle}>{subheader}</p>
                  <FlashUpdated versionId={versionId} />
                </div>
              </Row.Item>
              <Row.Item shrink>
                <IconButton
                  kind="action"
                  name={isOpen ? "chevron-up" : "chevron-down"}
                  onClick={(event: MouseEvent) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleChevronToggle();
                  }}
                />
              </Row.Item>
            </Row>
          </div>
        </ContentCard>
      </div>
    ),
    [handleChevronToggle, handleCardClick, subheader, title, versionId]
  );

  return (
    <div
      className={`${styles.previewCardWrapper} ${
        selected ? styles.previewCardSelected : ""
      } ${!collapsed ? styles.previewCardOpen : ""}`.trim()}
    >
      <Accordion isOpen={!collapsed} renderSummary={renderSummary}>
        <div className={styles.previewCardBody}>
          <ContentCard kind="bordered" paddingSize="l">
            <div className={styles.previewDetailsContainer}>
              <div>
                <p className={styles.previewDetailsHeader}>
                  {templateDetails.header}
                </p>
                {templateDetails.rows.map((row, i) => (
                  <p
                    key={`templateDetails-${row}-${i}`}
                    className={`${styles.previewDetailsValue} ${styles.ellipsisWrapper} ${styles.singleLine}`}
                  >
                    {row}
                  </p>
                ))}
              </div>
              <div>
                <p className={styles.previewDetailsHeader}>
                  {recipientDetails.header}
                </p>
                {recipientDetails.rows.map((row, i) => (
                  <p
                    key={`recipientDetails-${row}-${i}`}
                    className={styles.previewDetailsValue}
                  >
                    {row}
                  </p>
                ))}
              </div>
              <div>
                <p className={styles.previewDetailsHeader}>
                  {bankDetails.header}
                </p>
                <div className={styles.previewBankDetails}>
                  <div className={styles.previewInstitution}>
                    <div className={styles.previewInstitutionLogo}>
                      <span className="narmi-icon-bank" />
                    </div>
                  </div>
                  {bankDetails.rows.map((row, i) => (
                    <p
                      key={`bankDetails-${row}-${i}`}
                      className={styles.previewDetailsValue}
                    >
                      {i > 0 ? `  ${row}` : row}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </ContentCard>
        </div>
      </Accordion>
    </div>
  );
}

export default memo(TemplateListCard);
